<!--
  SettlementStartModal.vue
  콜봇 월 정산 · 병원 상세의 「정산 시작일」 수정 모달 — 계획서 ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-4,
  call-api 요청서 ~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md 요청 계약 3절(GET·preview·PUT settlement-start).

  ReopenModal 과 같은 구조(셸 SettlementDetail.vue 가 가진다 — 헤더 칩·정산 탭 빈 상태 카드는 'open-start' 만 보낸다).
  - 열면 GET settlement-start 로 지금 값·rev·변경 이력(최근 20, 접힘)을 읽는다. 404(라우트 없음 = 옛 call-api)면
    START_UNIMPLEMENTED_TEXT 를 보이고 저장을 막는다.
  - 새 시작일(DatePicker, 문자열 값 'YYYY-MM-DD' — dayjs 를 import 하지 않는다) 또는 「미설정으로 되돌리기」, 이유(필수),
    메모(필수 · 500자). 날짜·되돌리기가 바뀌면 입력이 멈춘 뒤(400ms) 미리보기를 한 번 받아 바뀌는 달 표를 그린다.
  - 막힘(blockers)은 표 위에 해결 방법 문장 + [그 달 열기]. 막힘이 있으면 [저장]을 막는다.
  - 지울 정산서(discard_required)가 있으면 상태·합계·사람이 고침 문장과 확인 체크. 저장은 미리보기에서 본 rev 로
    acknowledge_discard 를 보낸다(그사이 바뀐 정산서는 서버가 지우지 않는다).
  - 저장(PUT) 성공 → emit('saved', 응답) — 셸이 상세를 다시 받는다. 응답의 영향 달에 안내할 것이 있으면 결과 단계로
    (close_needed → [한 회사 마감 실행] = 기존 POST close-runs, discard_conflict → [남은 정산서 지우기] = 같은 시작일로
    미리보기 다시, confirmed_during_change → [그 달 열기]). 없으면 알리고 닫는다.
  - 저장 실패는 startErrorText 의 next 로 나눈다: reload(REV_CHANGED — 다시 읽고 미리보기 다시) · repreview(지울 목록이
    바뀜) · blocked(막힘 — 미리보기 다시) · unimplemented · refresh(AUDIT_UNAVAILABLE·PERM_UNAVAILABLE — 오류 줄 + 지금 값(rev)만
    다시 읽음. 서버가 보상 CAS 로 rev 를 올렸을 수 있어 그대로 다시 보내면 거짓 REV_CHANGED 가 난다. 입력·확인 체크 유지) ·
    keep(모달 안 오류 줄, 입력 유지).
  - 미리보기(GET) 실패는 startPreviewErrorText — 저장 문구가 아닌 '바뀌는 달을 불러오지 못했습니다' + [다시 불러오기](같은 입력).
  - cleanup prop(정산 탭 [남은 정산서 지우기]): 날짜를 지금 값으로 고정하고 미리보기만 다시 받는다.
  - 저장 중에는 [저장]이 loading 이고 두 번 누르기·닫기를 막는다.

  - props: open · companyId · month(상세의 달) · companyName · api · cleanup
  - emits: close · saved(putResponse) · open-month(month)
-->
<template>
  <a-modal :open="open" :footer="null" :mask-closable="false" width="640px" @cancel="handleCancel">
    <template #title>
      <div class="modal-title">{{ cleanup ? '남은 정산서 지우기' : '정산 시작일' }}</div>
    </template>

    <div class="modal-content">
      <div class="start-modal">
        <p class="start-modal__target">{{ targetText }}</p>

        <a-alert v-if="unimplemented" type="info" show-icon :message="START_UNIMPLEMENTED_TEXT" />

        <a-skeleton v-else-if="docLoading && !doc" active :title="false" :paragraph="{ rows: 3 }" />

        <div v-else-if="docError && !doc" class="start-modal__error" role="alert">
          <span>{{ docError }}</span>
          <a-button size="small" @click="loadDoc">다시 시도</a-button>
        </div>

        <!-- 저장 결과 안내 -->
        <template v-else-if="stage === 'result'">
          <p class="start-modal__current">{{ currentText }}</p>
          <ul class="start-notices" aria-label="저장 결과">
            <li v-for="n in notices" :key="`${n.kind}:${n.month}`" class="start-notices__item" :class="`is-${noticeTone(n)}`">
              <span class="start-notices__text">{{ n.text }}</span>
              <a-button
                v-if="n.action === 'close_company'"
                size="small"
                :loading="closingMonth === n.month"
                :disabled="closedMonths.has(n.month) || (!!closingMonth && closingMonth !== n.month)"
                @click="runCloseCompany(n.month)"
              >
                {{ closedMonths.has(n.month) ? '마감 실행함' : '한 회사 마감 실행' }}
              </a-button>
              <a-button v-else-if="n.action === 'cleanup'" size="small" @click="startCleanupAgain">
                남은 정산서 지우기
              </a-button>
              <a-button v-else-if="n.action === 'reopen'" size="small" @click="openMonth(n.month)">그 달 열기</a-button>
            </li>
          </ul>
        </template>

        <!-- 입력 -->
        <template v-else-if="doc">
          <p class="start-modal__current">{{ currentText }}</p>
          <p v-if="cleanup" class="start-modal__hint">지금 시작일은 그대로 두고, 시작 전 달에 남은 정산서만 지웁니다.</p>

          <div class="start-form">
            <div class="start-form__row">
              <span :id="`${uid}-date`" class="start-form__label">새 시작일</span>
              <div class="start-form__control start-form__control--inline">
                <a-date-picker
                  v-model:value="startDate"
                  value-format="YYYY-MM-DD"
                  format="YYYY-MM-DD"
                  placeholder="날짜 고르기"
                  :disabled-date="disabledDate"
                  :disabled="clear || cleanup || saving"
                  :aria-labelledby="`${uid}-date`"
                />
                <a-checkbox v-model:checked="clear" :disabled="cleanup || saving || !currentStartDate">
                  미설정으로 되돌리기
                </a-checkbox>
              </div>
            </div>
            <p class="start-form__help">{{ rangeText }}</p>

            <div class="start-form__row">
              <span class="start-form__label">이유 <em>*</em></span>
              <div class="start-form__control">
                <a-select
                  v-model:value="reasonCode"
                  class="start-form__select"
                  :options="reasonOptions"
                  placeholder="바꾸는 이유"
                  :disabled="saving"
                  aria-label="바꾸는 이유"
                />
              </div>
            </div>

            <div class="start-form__row">
              <span class="start-form__label">메모 <em>*</em></span>
              <div class="start-form__control">
                <a-textarea
                  v-model:value="memo"
                  :rows="2"
                  :maxlength="START_MEMO_MAX"
                  show-count
                  :disabled="saving"
                  placeholder="예: 10/1~10/9 테스트, 계약 시작 10/10"
                  aria-label="메모(필수)"
                />
              </div>
            </div>
          </div>

          <!-- 바뀌는 달 -->
          <section v-if="previewWanted" class="start-preview" :aria-busy="previewLoading ? 'true' : 'false'">
            <h3 class="start-preview__title">
              바뀌는 달
              <a-spin v-if="previewLoading" size="small" class="start-preview__spin" />
            </h3>

            <p v-if="previewError" class="start-modal__line is-danger start-preview__error" role="alert">
              <span>{{ previewError }}</span>
              <a-button v-if="previewRetry" size="small" :disabled="saving" @click="retryPreview">다시 불러오기</a-button>
            </p>

            <template v-else-if="view">
              <ul v-if="view.blockers.length" class="start-blockers" role="alert">
                <li v-for="b in view.blockers" :key="b.code" class="start-blockers__item">
                  <span>{{ b.text }}</span>
                  <a-button v-if="b.openMonth" size="small" @click="openMonth(b.openMonth)">그 달 열기</a-button>
                </li>
              </ul>

              <p v-if="!view.rows.length" class="start-modal__hint">바뀌는 달이 없습니다.</p>
              <div v-else class="start-preview__scroll">
                <table class="start-table">
                  <caption class="start-modal__sr">시작일을 바꾸면 달라지는 달</caption>
                  <thead>
                    <tr>
                      <th scope="col">달</th>
                      <th scope="col">지금</th>
                      <th scope="col">바뀐 뒤</th>
                      <th scope="col">정산서</th>
                      <th scope="col">할 일</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in view.rows" :key="r.month" :class="`is-${r.tone}`">
                      <th scope="row">{{ r.month }}</th>
                      <td>{{ r.beforeText }}</td>
                      <td>{{ r.afterText }}</td>
                      <td>{{ r.statementText }}</td>
                      <td class="start-table__action">{{ r.actionText }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="view.discards.length" class="start-discards">
                <p v-for="d in view.discards" :key="`${d.month}:${d.rev}`" class="start-discards__text">
                  <WarningOutlined aria-hidden="true" />
                  <span>{{ d.text }}</span>
                </p>
                <a-checkbox v-model:checked="acknowledged" :disabled="saving">
                  위 정산서를 지우는 것을 확인했습니다.
                </a-checkbox>
              </div>
            </template>
          </section>

          <p v-if="saveError" class="start-modal__line is-danger" role="alert">{{ saveError }}</p>
        </template>

        <!-- 변경 이력(최근 20) — 접힘 -->
        <details v-if="doc && historyRows.length" class="start-history">
          <summary>변경 이력(최근 {{ historyRows.length }})</summary>
          <ol class="start-history__list">
            <li v-for="(h, i) in historyRows" :key="`${h.rev}:${i}`" class="start-history__item">
              <div class="start-history__head">
                <span class="start-history__change">{{ h.beforeText }} → {{ h.afterText }}</span>
                <span class="start-history__meta">{{ h.atText }} · {{ h.actor }} · {{ h.reasonText }}</span>
              </div>
              <p v-if="h.memo" class="start-history__memo">{{ h.memo }}</p>
              <p v-if="h.discardedText" class="start-history__memo">{{ h.discardedText }}</p>
            </li>
          </ol>
        </details>
      </div>

      <div class="modal-footer">
        <template v-if="stage === 'result'">
          <a-button type="primary" class="yellow-button" @click="handleCancel">닫기</a-button>
        </template>
        <template v-else>
          <a-button :disabled="saving" @click="handleCancel">취소</a-button>
          <a-tooltip :title="saveDisabledReason || undefined">
            <span class="start-modal__btn-wrap">
              <a-button
                type="primary"
                class="yellow-button"
                :loading="saving"
                :disabled="!!saveDisabledReason"
                @click="handleSubmit"
              >
                저장
              </a-button>
            </span>
          </a-tooltip>
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { WarningOutlined } from '@ant-design/icons-vue';

import { monthLabel } from '../service/settlementFormat';
import { BILLING_INDEXES_NOT_READY_TEXT } from '../service/settlementRules';
import {
  START_MEMO_MAX,
  START_REASONS,
  START_UNIMPLEMENTED_TEXT,
  buildSettlementStartBody,
  isDateKey,
  isStartDateAllowed,
  startCurrentText,
  startDateRange,
  startErrorText,
  startFormError,
  startHistoryRows,
  startPreviewErrorText,
  startPreviewRows,
  startResultNotices,
} from '../service/settlementStart';
import { getCallApiErrorCode, isCallApiUnimplemented, parseCallApiError } from '@/views/Callbot/service/callApiError';

/** 날짜를 고른 뒤 이만큼 입력이 멈추면 미리보기를 한 번 받는다 */
const PREVIEW_DELAY_MS = 400;

const props = defineProps({
  open: { type: Boolean, required: true },
  companyId: { type: String, required: true },
  /** 상세의 달 'YYYY-MM'(KST) — 머리 줄에만 쓴다 */
  month: { type: String, required: true },
  companyName: { type: String, default: '' },
  /** settlementApi 또는 목(getSettlementApi()) */
  api: { type: Object, required: true },
  /** [남은 정산서 지우기] — 날짜를 지금 값으로 고정하고 미리보기만 다시 받는다 */
  cleanup: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'saved', 'open-month']);

const uid = `st-start-${getCurrentInstance()?.uid ?? 0}`;

// a-select 에는 얼린 공유 상수의 복사본을 넘긴다
const reasonOptions = START_REASONS.map((option) => ({ ...option }));

// ---------------------------------------------------------------------------
// 상태
// ---------------------------------------------------------------------------

/** 'form' | 'result' */
const stage = ref('form');

/** GET settlement-start 응답(PUT 성공 뒤에는 PUT 응답) */
const doc = ref(null);
const docLoading = ref(false);
const docError = ref('');
const unimplemented = ref(false);

const clear = ref(false);
/** 'YYYY-MM-DD' | null */
const startDate = ref(null);
const reasonCode = ref(null);
const memo = ref('');
const acknowledged = ref(false);

/** 미리보기 원본과 그것을 받은 입력 키 — 저장은 지금 입력과 같은 키의 미리보기일 때만 */
const preview = ref(null);
const previewKey = ref(null);
const previewLoading = ref(false);
const previewError = ref('');
/** 미리보기 실패가 같은 입력으로 다시 받으면 풀릴 수 있다 — [다시 불러오기] */
const previewRetry = ref(false);

const saving = ref(false);
const saveError = ref('');

/** 결과 단계 안내(startResultNotices) */
const notices = ref([]);
const closingMonth = ref(null);
const closedMonths = ref(new Set());

const currentStartDate = computed(() => doc.value?.start_date ?? null);
const currentText = computed(() => (doc.value ? startCurrentText(doc.value) : ''));
const historyRows = computed(() => (doc.value ? startHistoryRows(doc.value) : []));

const targetText = computed(() => {
  const name = props.companyName || props.companyId;
  return `${name} · ${monthLabel(props.month)} 상세에서 엶`;
});

const rangeText = computed(() => {
  const { min, max } = startDateRange(Date.now());
  return `${min} ~ ${max} 사이에서 고릅니다. 시작일 00:00(KST)부터 청구합니다.`;
});

/** DatePicker 가 넘기는 날짜 객체(antd 안의 dayjs)는 .format 만 부른다 */
function disabledDate(current) {
  if (!current || typeof current.format !== 'function') return false;
  return !isStartDateAllowed(current.format('YYYY-MM-DD'), Date.now());
}

/** 지금 입력이 미리보기를 받을 만한가 — 되돌리기이거나 범위 안 날짜 */
const inputKey = computed(() => {
  if (clear.value) return 'null';
  if (isDateKey(startDate.value) && isStartDateAllowed(startDate.value, Date.now())) return startDate.value;
  return null;
});

const previewWanted = computed(() => inputKey.value !== null);

const view = computed(() => {
  if (!preview.value || previewKey.value !== inputKey.value) return null;
  return startPreviewRows(preview.value);
});

const discards = computed(() => view.value?.discards ?? []);

const saveDisabledReason = computed(() => {
  if (unimplemented.value) return START_UNIMPLEMENTED_TEXT;
  if (!doc.value) return docLoading.value ? '지금 값을 불러오는 중입니다.' : '지금 값을 불러오지 못했습니다.';
  if (view.value && !view.value.canSave) return '위 막힘을 먼저 풀어야 저장할 수 있습니다.';
  return '';
});

// ---------------------------------------------------------------------------
// 열기 · 지금 값 읽기
// ---------------------------------------------------------------------------

let docSeq = 0;
let previewSeq = 0;
let previewTimer = null;

function clearPreviewTimer() {
  if (previewTimer) clearTimeout(previewTimer);
  previewTimer = null;
}

function resetAll() {
  stage.value = 'form';
  doc.value = null;
  docError.value = '';
  unimplemented.value = false;
  clear.value = false;
  startDate.value = null;
  reasonCode.value = null;
  memo.value = '';
  acknowledged.value = false;
  preview.value = null;
  previewKey.value = null;
  previewLoading.value = false;
  previewError.value = '';
  previewRetry.value = false;
  saveError.value = '';
  notices.value = [];
  closingMonth.value = null;
  closedMonths.value = new Set();
  clearPreviewTimer();
  previewSeq += 1;
}

/**
 * 시작일 GET — 실패는 안에서 처리한다
 * @param {{ keepInput?: boolean }} [opts] keepInput: 입력·미리보기·확인 체크를 그대로 두고 rev 만 새로(남은 정산서 지우기도
 *   날짜를 다시 고정하지 않는다)
 */
async function loadDoc({ keepInput = false } = {}) {
  const seq = ++docSeq;
  docLoading.value = true;
  docError.value = '';
  try {
    const res = await props.api.getSettlementStart(props.companyId);
    if (seq !== docSeq) return;
    doc.value = res && typeof res === 'object' ? res : { start_date: null, rev: 0, history: [] };
    if (props.cleanup && !keepInput) fixToCurrent();
  } catch (err) {
    if (seq !== docSeq) return;
    if (isCallApiUnimplemented(err)) unimplemented.value = true;
    else docError.value = parseCallApiError(err, '정산 시작일을 불러오지 못했습니다.');
  } finally {
    if (seq === docSeq) docLoading.value = false;
  }
}

/** [남은 정산서 지우기] — 날짜를 지금 값으로 두고 바로 미리보기 */
function fixToCurrent() {
  const current = currentStartDate.value;
  clear.value = current === null;
  startDate.value = current;
  acknowledged.value = false;
  requestPreview({ immediate: true });
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      resetAll();
      void loadDoc();
    } else {
      // 닫히면 늦게 온 응답을 버린다
      docSeq += 1;
      previewSeq += 1;
      clearPreviewTimer();
    }
  },
  { immediate: true }
);

onBeforeUnmount(clearPreviewTimer);

// ---------------------------------------------------------------------------
// 미리보기 — 입력이 멈춘 뒤 한 번
// ---------------------------------------------------------------------------

async function runPreview() {
  const key = inputKey.value;
  if (key === null || !doc.value || unimplemented.value) return;
  const seq = ++previewSeq;
  previewLoading.value = true;
  previewError.value = '';
  previewRetry.value = false;
  try {
    const res = await props.api.previewSettlementStart(props.companyId, key === 'null' ? null : key);
    if (seq !== previewSeq) return;
    preview.value = res && typeof res === 'object' ? res : null;
    previewKey.value = key;
    acknowledged.value = false;
  } catch (err) {
    if (seq !== previewSeq) return;
    preview.value = null;
    previewKey.value = null;
    const e = startPreviewErrorText(err);
    if (e.unimplemented) unimplemented.value = true;
    else {
      previewError.value = e.text;
      previewRetry.value = e.retry;
    }
  } finally {
    if (seq === previewSeq) previewLoading.value = false;
  }
}

function requestPreview({ immediate = false } = {}) {
  clearPreviewTimer();
  previewSeq += 1; // 받는 중인 앞 미리보기는 버린다
  previewLoading.value = false;
  if (inputKey.value === null) {
    preview.value = null;
    previewKey.value = null;
    previewError.value = '';
    previewRetry.value = false;
    return;
  }
  if (immediate) {
    void runPreview();
    return;
  }
  previewLoading.value = true;
  previewTimer = setTimeout(() => {
    previewTimer = null;
    void runPreview();
  }, PREVIEW_DELAY_MS);
}

/** [다시 불러오기] — 같은 입력으로 미리보기를 바로 다시 받는다 */
function retryPreview() {
  if (saving.value) return;
  requestPreview({ immediate: true });
}

// sync — fixToCurrent 가 값을 바꾼 바로 뒤 requestPreview({ immediate }) 를 부르면 그것이 이긴다(400ms 를 기다리지 않게)
watch(
  inputKey,
  () => {
    if (!props.open || stage.value !== 'form' || !doc.value) return;
    saveError.value = '';
    requestPreview();
  },
  { flush: 'sync' }
);

// ---------------------------------------------------------------------------
// 저장
// ---------------------------------------------------------------------------

async function handleSubmit() {
  if (saving.value || saveDisabledReason.value) return;
  const formError = startFormError({
    clear: clear.value,
    startDate: startDate.value,
    reasonCode: reasonCode.value,
    memo: memo.value,
    discards: discards.value,
    acknowledged: acknowledged.value,
    nowMs: Date.now(),
  });
  if (formError) {
    message.warning(formError);
    return;
  }
  // 미리보기를 받지 못했다 — '불러오는 중'이 아니라 그 오류와 해결 방법을 말한다
  if (!previewLoading.value && previewError.value) {
    message.warning(previewError.value);
    return;
  }
  // 지울 정산서 목록은 지금 입력의 미리보기로만 정한다 — 받는 중이면 기다리게 한다
  if (previewLoading.value || !view.value) {
    message.warning('바뀌는 달을 불러오는 중입니다. 표가 나온 뒤 다시 누르세요.');
    return;
  }
  if (!view.value.canSave) {
    message.warning('위 막힘을 먼저 풀어야 저장할 수 있습니다.');
    return;
  }

  saving.value = true;
  saveError.value = '';
  try {
    const body = buildSettlementStartBody({
      clear: clear.value,
      startDate: startDate.value,
      rev: doc.value?.rev ?? 0,
      reasonCode: reasonCode.value,
      memo: memo.value,
      discards: discards.value,
    });
    const res = await props.api.putSettlementStart(props.companyId, body);
    emit('saved', res);
    if (res && typeof res === 'object') doc.value = res;
    const list = startResultNotices(res);
    if (!list.length) {
      message.success('정산 시작일을 저장했습니다.');
      emit('close');
      return;
    }
    message.success('정산 시작일을 저장했습니다. 아래 안내를 확인하세요.');
    notices.value = list;
    stage.value = 'result';
  } catch (err) {
    await handleSaveError(err);
  } finally {
    saving.value = false;
  }
}

async function handleSaveError(err) {
  const e = startErrorText(err);
  switch (e.next) {
    case 'reload':
      // 다른 사람이 먼저 바꿨다 — 지금 값(rev)을 다시 읽고 같은 입력으로 미리보기 다시
      message.warning(e.text);
      await loadDoc();
      if (props.cleanup) break; // fixToCurrent 가 미리보기를 다시 받았다
      requestPreview({ immediate: true });
      break;
    case 'repreview':
      message.warning(e.text);
      requestPreview({ immediate: true });
      break;
    case 'blocked':
      // 그사이 막힘이 생겼다 — 미리보기를 다시 받아 표 위에 막힘을 보인다
      saveError.value = e.text;
      requestPreview({ immediate: true });
      break;
    case 'refresh':
      // 서버가 바꾸지 않았다(보상 CAS 로 되돌렸으면 rev 가 올랐다) — 오류 줄은 두고 rev 만 다시 읽는다. 입력·확인 체크는 그대로라
      // 같은 [저장]을 다시 누르면 된다
      saveError.value = e.text;
      await loadDoc({ keepInput: true });
      break;
    case 'unimplemented':
      unimplemented.value = true;
      break;
    default:
      saveError.value = e.text;
      break;
  }
}

// ---------------------------------------------------------------------------
// 결과 단계 — 한 회사 마감 · 남은 정산서 지우기 · 그 달 열기
// ---------------------------------------------------------------------------

function noticeTone(n) {
  if (n.kind === 'discard_conflict' || n.kind === 'confirmed_during_change') return 'warn';
  if (n.kind === 'close_needed') return 'action';
  return 'info';
}

async function runCloseCompany(month) {
  if (closingMonth.value || closedMonths.value.has(month)) return;
  closingMonth.value = month;
  try {
    await props.api.startCloseRun({ month, companyId: props.companyId });
    closedMonths.value = new Set([...closedMonths.value, month]);
    message.success(`${month} 이 병원 마감을 실행했습니다. 잠시 후 그 달 상세에서 결과를 확인하세요.`);
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (code === 'MONTH_NOT_ENDED') {
      message.warning('아직 끝나지 않은 달은 마감을 실행할 수 없습니다. 달이 끝난 뒤 다시 실행하세요.');
    } else if (code === 'BILLING_INDEXES_NOT_READY') {
      message.warning(BILLING_INDEXES_NOT_READY_TEXT);
    } else {
      message.error(parseCallApiError(err, '마감을 실행하지 못했습니다.'));
    }
  } finally {
    closingMonth.value = null;
  }
}

/** discard_conflict — 같은 시작일로 미리보기를 다시 받아 같은 화면(확인 체크 포함)으로 다시 저장한다 */
function startCleanupAgain() {
  stage.value = 'form';
  notices.value = [];
  saveError.value = '';
  fixToCurrent();
}

function openMonth(month) {
  if (!month || saving.value) return;
  emit('open-month', month);
}

function handleCancel() {
  if (saving.value) return;
  emit('close');
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

@include st-modal;
@include yellow-button;

.start-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  font-size: 13px;
  color: $st-text;

  &__target {
    margin: 0;
    color: $st-muted;
    font-size: 12.5px;
  }

  &__current {
    margin: 0;
    font-weight: 600;
  }

  &__hint {
    margin: 0;
    color: $st-text-secondary;
    font-size: 12.5px;
  }

  &__line {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    line-height: 1.6;

    &.is-danger {
      border: 1px solid $st-danger-border;
      background: $st-danger-bg;
      color: $st-danger;
    }
  }

  &__error {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    color: $st-danger;
  }

  &__btn-wrap {
    display: inline-block;
  }

  &__sr {
    @include st-sr-only;
  }
}

.start-form {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__row {
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr);
    align-items: start;
    gap: 8px;
    margin: 0;
  }

  &__label {
    padding-top: 5px;
    font-weight: 600;

    em {
      font-style: normal;
      color: $st-danger;
    }
  }

  &__control {
    min-width: 0;

    &--inline {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
    }
  }

  &__select {
    width: 220px;
    max-width: 100%;
  }

  &__help {
    margin: -4px 0 0 96px;
    color: $st-muted;
    font-size: 12px;
  }
}

.start-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 600;
  }

  &__spin {
    line-height: 1;
  }

  &__error {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
  }

  &__scroll {
    overflow-x: auto;
  }
}

.start-blockers {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;

  &__item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 8px 12px;
    border: 1px solid $st-danger-border;
    border-radius: 8px;
    background: $st-danger-bg;
    color: $st-danger;
    line-height: 1.6;
  }
}

.start-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid $st-border !important;
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    color: $st-text-secondary;
    font-weight: 600;
    background: $st-bg;
  }

  tbody th {
    font-weight: 600;
  }

  tr.is-muted td {
    color: $st-muted;
  }

  tr.is-warn .start-table__action {
    color: $st-warning;
    font-weight: 600;
  }

  tr.is-danger .start-table__action {
    color: $st-danger;
    font-weight: 600;
  }

  tr.is-ok .start-table__action {
    color: $st-ok;
  }
}

.start-discards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid $st-warning-border;
  border-radius: 8px;
  background: $st-warning-bg;

  &__text {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 0;
    line-height: 1.6;

    :deep(.anticon) {
      color: $st-warning;
    }
  }
}

.start-notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  &__item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 10px 12px;
    border: 1px solid $st-border-strong;
    border-radius: 8px;
    background: $st-bg;
    line-height: 1.6;

    &.is-action {
      border-color: $st-accent-border;
      background: $st-accent-bg;
    }

    &.is-warn {
      border-color: $st-warning-border;
      background: $st-warning-bg;
    }
  }

  &__text {
    flex: 1 1 320px;
    min-width: 0;
  }
}

.start-history {
  font-size: 12.5px;

  summary {
    color: $st-text-secondary;
    cursor: pointer;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 240px;
    margin: 8px 0 0;
    padding: 0 0 0 4px;
    overflow-y: auto;
    list-style: none;
  }

  &__item {
    padding-bottom: 8px;
    border-bottom: 1px solid $st-border;
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
  }

  &__change {
    font-weight: 600;
  }

  &__meta {
    color: $st-muted;
  }

  &__memo {
    margin: 2px 0 0;
    color: $st-text-secondary;
    word-break: break-all;
  }
}
</style>
